import { SkuAnnotationType, type SkuAnnotation } from '@rta/rta-blueprint-library';

import type { StyleRunPatch } from './apply-style-run';

/** The white a fillable annotation falls back to when it has no colour of its own */
export const DEFAULT_ANNOTATION_FILL = '#ffffff';

/** The ink a text box's text and any annotation's stroke fall back to */
export const DEFAULT_ANNOTATION_INK = '#333333';

const SKU_SURFACE_STROKE_WIDTH = 0.3;
const PAGE_SURFACE_STROKE_WIDTH = 2;

/** Anything carrying the paint fields: a SkuAnnotation instance, a serialized record or a preset record */
export type AnnotationPaintSource = {
	type?: SkuAnnotationType;
	color?: string;
	backgroundColor?: string;
	fillOpacity?: number;
	strokeWidth?: number;
	pageNumber?: number;
};

/**
 * The two paint channels have one encoding each, for EVERY annotation type: `fillOpacity === 0` is "no
 * fill" and `strokeWidth === 0` is "no border/line". The colour fields are only ever colours, so turning a
 * channel off remembers what it was painted with and turning it back on restores it.
 */
export const isAnnotationFilled = (annotation: AnnotationPaintSource): boolean => (annotation.fillOpacity ?? 0) > 0;

export const isAnnotationStroked = (annotation: AnnotationPaintSource): boolean => (annotation.strokeWidth ?? 0) > 0;

/** The colour channels a picker can preview live, kept apart so a border drag never repaints the text */
export type AnnotationPreviewPatch = StyleRunPatch & {
	fillColor?: string | null;
	strokeColor?: string | null;
};

export type AnnotationFillPaint = {
	fill: string;
	fillOpacity: number;
};

export type GetAnnotationFillPaintParams = {
	annotation: AnnotationPaintSource;
	/** The colour a fill picker is still dragging; it paints even on an annotation whose fill is off */
	previewColor?: string | null;
};

export const getAnnotationFillPaint = (params: GetAnnotationFillPaintParams): AnnotationFillPaint => {
	const { annotation, previewColor = null } = params;
	const { backgroundColor, fillOpacity = 0 } = annotation;

	return {
		fill: previewColor || backgroundColor || DEFAULT_ANNOTATION_FILL,
		fillOpacity: previewColor && fillOpacity === 0 ? 1 : fillOpacity
	};
};

export type AnnotationStrokePaint = {
	/** 'none' when the channel is off, so the stroke is neither painted nor hit-tested */
	stroke: string;
	strokeWidth: number;
	strokeDasharray: string | undefined;
};

export type GetAnnotationStrokePaintParams = {
	annotation: AnnotationPaintSource;
	/** The width to draw at, in the calling surface's own units */
	width: number;
	dasharray?: string;
	previewColor?: string | null;
};

export const getAnnotationStrokePaint = (params: GetAnnotationStrokePaintParams): AnnotationStrokePaint => {
	const { annotation, width, dasharray, previewColor = null } = params;
	if (!isAnnotationStroked(annotation)) return { stroke: 'none', strokeWidth: 0, strokeDasharray: undefined };

	return {
		stroke: previewColor || annotation.color || DEFAULT_ANNOTATION_INK,
		strokeWidth: width,
		strokeDasharray: dasharray
	};
};

/** The weight the annotation's own surface creates strokes at -- also what re-enabling a border restores */
export const getSurfaceStrokeWidth = (annotation: AnnotationPaintSource): number => (
	(annotation.pageNumber ?? 0) > 0 ? PAGE_SURFACE_STROKE_WIDTH : SKU_SURFACE_STROKE_WIDTH
);

export type SetAnnotationPaintParams = {
	annotation: SkuAnnotation;
	/** `null` is the none swatch: no fill / no border */
	color: string | null;
};

export const setAnnotationFill = (params: SetAnnotationPaintParams): void => {
	const { annotation, color } = params;
	if (color === null) {
		annotation.fillOpacity = 0;
		return;
	}
	annotation.backgroundColor = color;
	if (annotation.fillOpacity === 0) annotation.fillOpacity = 1;
};

export const setAnnotationStroke = (params: SetAnnotationPaintParams): void => {
	const { annotation, color } = params;
	if (color === null) {
		annotation.strokeWidth = 0;
		return;
	}
	annotation.color = color;
	if (annotation.strokeWidth === 0) annotation.strokeWidth = getSurfaceStrokeWidth(annotation);
};

/** Which style channels a type has -- the one answer the toolbar gates its groups on and the renderers draw handles from */
export type AnnotationCapabilities = {
	text: boolean;
	fill: boolean;
	stroke: boolean;
	/** Whether the stroke WEIGHT is honoured; a text box border is a fixed hairline on both surfaces, so only its 0 is read */
	strokeWeight: boolean;
	arrowheads: boolean;
	rotate: boolean;
};

const NO_CAPABILITIES: AnnotationCapabilities = { text: false, fill: false, stroke: false, strokeWeight: false, arrowheads: false, rotate: false };

const SHAPE_CAPABILITIES: AnnotationCapabilities = { text: false, fill: true, stroke: true, strokeWeight: true, arrowheads: false, rotate: true };

const ANNOTATION_CAPABILITIES: Record<SkuAnnotationType, AnnotationCapabilities> = {
	[SkuAnnotationType.TextBox]: { text: true, fill: true, stroke: true, strokeWeight: false, arrowheads: false, rotate: true },
	[SkuAnnotationType.Rectangle]: SHAPE_CAPABILITIES,
	[SkuAnnotationType.Ellipse]: SHAPE_CAPABILITIES,
	[SkuAnnotationType.Line]: { text: false, fill: false, stroke: true, strokeWeight: true, arrowheads: true, rotate: true },
	[SkuAnnotationType.Doodle]: { text: false, fill: false, stroke: true, strokeWeight: true, arrowheads: false, rotate: false }
};

export const getAnnotationCapabilities = (type: SkuAnnotationType | null | undefined): AnnotationCapabilities => (
	type ? ANNOTATION_CAPABILITIES[type] : NO_CAPABILITIES
);
